import { Button, ConfigProvider, Flex, Layout, Tabs, Typography } from 'antd';
import React, { useState, useContext, useEffect } from 'react';
import './VehicleMonitoring.css';
import Monitoring from './components/Monitoring';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import { useNotification } from '../../context/NotificationContext';
import { ReactComponent as cancel } from '../../assets/icons/cancel.svg';
import Icon from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { ReactComponent as cancelBlack } from '../../assets/icons/cancel-black.svg';
import { DARK, PAGE_KEYS, VEHICLE_STATUS } from '../../utilities/constants';
import { ReactComponent as BroadcastSvg } from '../../assets/icons/broadcast-blue.svg';
import { ReactComponent as BroadcastLightSvg } from '../../assets/icons/broadcast-blue-light.svg';
import { ReactComponent as BroadcastDisabledDarkSvg } from '../../assets/icons/broadcast-disabled-dark.svg';
import { ReactComponent as BroadcastDisabledLightSvg } from '../../assets/icons/broadcast-disabled-ligth.svg';
import BroadcastModal from '../Broadcast/components/BroadcastModal';
import { formatDateTime } from '../../utilities/commonFunctions';
import { UserRolesAndPermissionsContext } from '../../context/UserRolesAndPermissionsContext';

const { Text } = Typography;
const { Content } = Layout;

const VehicleMonitoring = () => {
  const { addErrorMessage, removeErrorMessage } =
    useContext(ErrorMessageContext);

  const { theme } = useTheme();
  const [activeKey, setActiveKey] = useState('monitoring');
  const [activeTabKey, setActiveTabKey] = useState(VEHICLE_STATUS.Active);
  const shouldShowTabHeaderActions =
    activeKey === 'monitoring' && activeTabKey === VEHICLE_STATUS.Active;
  const { isFirstLoginSession, cancelVehicleMonitoringNotification } =
    useNotification();

  const [lastUpdatedTime, setLastUpdatedTime] = useState(Date.now());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTripDetails, setSelectedTripDetails] = useState({});
  const [isTripListLoading, setIsTripListLoading] = useState(false);

  const { isViewOnly } = useContext(UserRolesAndPermissionsContext);
  const isBroadcastDisabled =
    !selectedTripDetails?.trip_id ||
    activeTabKey === VEHICLE_STATUS.Inactive ||
    activeKey === 'logs';

  const addNotification = () => {
    if (isFirstLoginSession?.vehicleMonitoring) {
      addErrorMessage(
        <Flex gap={16} align="center">
          <Text>The data shown here is of the last 24 hours.</Text>
          <Icon
            component={theme === DARK ? cancel : cancelBlack}
            onClick={() => {
              removeErrorMessage('vehicle-monitoring-notification');
              cancelVehicleMonitoringNotification();
            }}
          />
        </Flex>,
        'notify',
        'vehicle-monitoring-notification'
      );
    }
  };

  useEffect(() => {
    if (activeKey === 'logs') {
      removeErrorMessage('vehicle-monitoring-notification');
    } else {
      addNotification();
    }
  }, [activeKey, theme]);

  useEffect(() => {
    if (activeKey === 'monitoring') {
      addNotification();
    }
    return () => {
      removeErrorMessage('vehicle-monitoring-notification');
    };
  }, []);

  const handleBroadcastClick = () => {
    if (selectedTripDetails !== null) {
      setIsModalOpen(true);
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
            titleMarginTop: 0,
            fontSize: '1rem',
          },
          Layout: {
            siderBg: 'var(--secondary-fill-color, #2E3033)',
            headerBg: 'transparent',
            headerPadding: '0',
          },
          Form: {
            labelColonMarginInlineEnd: 0,
            labelColonMarginInlineStart: 0,
          },
        },
        token: {
          fontSize: '0.875rem',
        },
      }}
    >
      <Layout className="layout-page">
        <Content className="full-height">
          <Tabs
            type="card"
            size="large"
            className="full-height page-tabs"
            tabBarGutter="2rem"
            tabBarExtraContent={
              shouldShowTabHeaderActions ? (
                <Flex gap={24} align="center">
                  <Text className="last-updated-time">
                    {formatDateTime(lastUpdatedTime)}
                  </Text>
                  {!isViewOnly(PAGE_KEYS.BROADCAST) && (
                    <Button
                      onClick={handleBroadcastClick}
                      disabled={isBroadcastDisabled}
                    >
                      <Flex gap={8}>
                        <Icon
                          component={
                            isBroadcastDisabled
                              ? theme === DARK
                                ? BroadcastDisabledDarkSvg
                                : BroadcastDisabledLightSvg
                              : theme === DARK
                                ? BroadcastSvg
                                : BroadcastLightSvg
                          }
                          style={{ fontSize: '1.3rem' }}
                        />
                        Create Broadcast
                      </Flex>
                    </Button>
                  )}
                  <BroadcastModal
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    tripDetails={selectedTripDetails}
                  />
                </Flex>
              ) : null
            }
            tabBarStyle={{
              padding: '0 0 0 1rem',
            }}
            destroyOnHidden={true}
            defaultActiveKey="monitoring"
            activeKey={activeKey}
            items={[
              {
                key: 'monitoring',
                label: 'Monitoring',
                children: (
                  <Monitoring
                    pageActiveKey={activeKey}
                    setLastUpdatedTime={setLastUpdatedTime}
                    selectedTripDetails={selectedTripDetails}
                    setSelectedTripDetails={setSelectedTripDetails}
                    activeTabKey={activeTabKey}
                    setActiveTabKey={setActiveTabKey}
                    setIsTripListLoading={setIsTripListLoading}
                  />
                ),
              },
              {
                key: 'logs',
                label: 'Logs',
                children: (
                  <Monitoring
                    pageActiveKey={activeKey}
                    setLastUpdatedTime={setLastUpdatedTime}
                    selectedTripDetails={selectedTripDetails}
                    setSelectedTripDetails={setSelectedTripDetails}
                    activeTabKey={activeTabKey}
                    setActiveTabKey={setActiveTabKey}
                    setIsTripListLoading={setIsTripListLoading}
                  />
                ),
              },
            ]}
            onChange={nextActiveKey => {
              if (isTripListLoading) {
                return;
              }

              setActiveKey(nextActiveKey);
              setSelectedTripDetails({});
            }}
          />
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default React.memo(VehicleMonitoring);
